import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store/ducks';
import store from 'store';

import { IRequestFilter, IRequestProtocol, ITicketsData } from 'models/Ticket';
import { modelTicket } from './models';
import { IBatchRequestResponse } from 'models/Batch';

import Table from '..';
import TableSearchProtocol from 'components/TableSearchProtocol';

import KeycloakPubServiceInstance from 'services/keycloakpubsub.service';
import TicketServices from 'services/ticket.service';

import { DateOutputStr } from 'utils/DateOutput';
import { ITicketTable } from './interface';
import { validateDatePeriodExportData } from 'utils/Date';

const TicketsTable: React.FC<ITicketTable> = (props) => {

  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.user);

  const {
    showButton,
    trackingKey,
    showFilter,
    completeBlankSpaces,
    model,
    initialOrder,
    refreshTable,
    showExportButton,
    showProtocolsOnLoad,
    isPageTickets,
    statusTypes,
    currentTabId
  } = props;

  const [tickets, setTickets] = useState<IBatchRequestResponse<ITicketsData>>();
  const [tableFilter, setTableFilter] = useState({
    and: [
      {
        createdDate: {
          ge: new Date(
            `${DateOutputStr(
              getMinDate().toISOString(),
              'yyyy-MM-dd'
            )}`
          ),
        },
      },
      {
        createdDate: {
          le: new Date(
            `${DateOutputStr(
              getMaxDate().toISOString(),
              'yyyy-MM-dd'
            )}`
          ),
        },
      },
    ],
  });
  const [tableOrder, setTableOrder] = useState(initialOrder || 'createdDate desc');
  const [currentPage, setCurrentPage] = useState(0);

  const formatedModel: any = [...((model as any) || (modelTicket as any))];
  const [filterProtocols, setFilterProtocols] = useState<IRequestFilter>();
  const [onLoadPageTable, setOnLoadPageTable] = useState(showProtocolsOnLoad || false);

  const pageSizeProp = props.pageSize || 10;

  useEffect(() => {
    if (!currentTabId) return;
    dispatch({
      type: 'SET_CURRENT_TAB',
      data: currentTabId
    });
  }, []);

  const exportData = async () => {
    const closedProtocols = 2;
    if (statusTypes?.includes(closedProtocols) && !validateDatePeriodExportData(filterProtocols?.startPeriod, filterProtocols?.lastPeriod)) return;

    await new TicketServices().downloadData(filterProtocols || {});
  };

  const getTickets = useCallback(async () => {
    if (user?.userType === 'supplier') return;

    if (isPageTickets && !filterProtocols) return;
    if (!isPageTickets && !onLoadPageTable) return;

    setTickets(undefined);

    if (onLoadPageTable || isPageTickets) {
      if (isPageTickets) {
        if (filterProtocols) {
          const newFilterProtocols: IRequestFilter = filterProtocols;
          const orderProtocols = tableOrder.split(' ');

          if (newFilterProtocols) {
            newFilterProtocols.currentPage = currentPage;
            newFilterProtocols.sortBy = orderProtocols[0] || 'createdDate';
            newFilterProtocols.sortDirection = orderProtocols[1] || 'desc';
            newFilterProtocols.customerIds = store.getState().customer?.id || user?.selectedCustomerId || '';

            const newFilter: IRequestProtocol | undefined = await new TicketServices().filterProtocols(filterProtocols);
            if (newFilter) {
              const { data, pageSize, total, totalPages } = newFilter.data;

              if (totalPages && pageSize && total) {
                setTickets({ data, totalPages, pageSize, total });
              } else {
                setTickets({ data: [], totalPages: 0, pageSize: 0, total: 0 });
              }
            } else {
              setTickets({ data: [], totalPages: 0, pageSize: 0, total: 0 });
            }
          }
        }
      } else {
        const orderProtocols = tableOrder.split(' ');

        const newFilterProtocolsDefault: IRequestFilter = {
          keyword: '',
          bus: [],
          responsibleGroups: [],
          categories: [],
          reasons: [],
          subsidiaryIds: [],
          phaseStatus: [],
          brands: [],
          statusType: statusTypes,
          protocolNumber: '',
          startPeriod: `${DateOutputStr(getMinDate().toISOString(), 'yyyy-MM-dd')}`,
          lastPeriod: `${DateOutputStr(getMaxDate().toISOString(), 'yyyy-MM-dd')}`,
          currentPage: 0,
          pageSize: 8,
          sortBy: orderProtocols[0] || 'createdDate',
          sortDirection: orderProtocols[1] || 'desc',
          customerIds: store.getState().customer?.id || user?.selectedCustomerId || ''
        };
        const newFilter: IRequestProtocol | undefined = await new TicketServices().filterProtocols(newFilterProtocolsDefault);
        if (newFilter) {
          const { data, pageSize, total, totalPages } = newFilter.data;

          if (totalPages && pageSize && total) {
            setTickets({ data, totalPages, pageSize, total });
          } else {
            setTickets({ data: [], totalPages: 0, pageSize: 0, total: 0 });
          }
        } else {
          setTickets({ data: [], totalPages: 0, pageSize: 0, total: 0 });
        }
      }
    }

  }, [tableOrder, tableFilter, currentPage, store.getState().customer, filterProtocols, onLoadPageTable, isPageTickets, user?.selectedCustomerId, user?.userType]);

  const bottomButton = {
    label: '+ Ver todos os protocolos',
    linkTo: '/protocolos',
  };

  function getMinDate() {
    const minDate = new Date(new Date().toDateString());
    minDate.setMonth(minDate.getMonth() - 3);
    return minDate;
  }

  function getMaxDate() {
    return new Date(new Date().toDateString());
  }

  const TicketsTableRender = useMemo(
    () => (
      <Table
        trackingKey={trackingKey}
        data={tickets?.data}
        error={tickets?.error}
        tableOrder={tableOrder}
        setTableOrder={setTableOrder}
        onReload={getTickets}
        currentPage={currentPage}
        DropdownTemplate={props.DropdownTemplate || undefined}
        className="mt-3"
        totalPages={tickets?.totalPages}
        pageSize={pageSizeProp}
        bottomButton={showButton ? bottomButton : undefined}
        completeBlankSpaces={completeBlankSpaces || false}
        onPageChange={setCurrentPage}
        model={formatedModel}
        exportButton={
          showExportButton ?
            {
              show: showExportButton,
              onClick: exportData,
            } :
            undefined
        }
      />
    ),
    [tickets, user?.selectedCustomerId, tableOrder]
  );

  useEffect(() => {
    getTickets();
    KeycloakPubServiceInstance.subscribe(() => {
      getTickets();
    });
  }, [getTickets, refreshTable, user?.selectedCustomerId, filterProtocols]);

  return (
    <>
      {showFilter && (
        <TableSearchProtocol
          trackingKey="Grid Protocolos"
          dateKey="createdDate"
          setTableFilter={setTableFilter}
          tableOrderBy={tableOrder}
          setTableOrderBy={setTableOrder}
          statusLabel="Status do Protocolo"
          setCurrentPage={setCurrentPage}
          dateMin={getMinDate()}
          dateMax={getMaxDate()}
          showGroupSelect
          ShowStatusSelect
          clearBtn
          showBUs
          setOnLoadPageTable={setOnLoadPageTable}
          typeStatusProtocol={trackingKey.split(' ')[1]}
          showCategories
          showReasons
          showBrands
          showSubsidiaries
          showAdvancedFilters
          setFilterProtocols={setFilterProtocols}
          statusTypes={statusTypes}
        />
      )}
      {TicketsTableRender}
    </>
  );
};

export default TicketsTable;
